// 共用 2D 奶油粗框：插畫素材保留端點，文字與互動由各場景控制。
// === 使用圖片 ===
// - public/assets/ui/cream-button-red.png（key: CREAM_BUTTON_RED_KEY）— 霧面紅按鈕
// - public/assets/ui/cream-button-green.png（key: CREAM_BUTTON_GREEN_KEY）— 霧面綠按鈕
// - public/assets/ui/cream-panel.png（key: CREAM_PANEL_KEY）— 奶油粗框、綠色紙感內底
// - public/assets/ui/cream-mascot-peek.png（key: CREAM_MASCOT_PEEK_KEY）— 探頭小牌
// 圓點與鍵盤焦點提示使用 Phaser.Graphics；不影響原有 hit area。

import Phaser from 'phaser';
import { animateTo, Motion, reducedMotion } from './motion';
import {
  CREAM_BUTTON_RED_KEY,
  CREAM_BUTTON_GREEN_KEY,
  CREAM_PANEL_KEY,
  CREAM_MASCOT_PEEK_KEY,
  CREAM_ASSET_FRAME,
} from '@scenes/boot-scene';

const CREAM = 0xffedc9; // 焦點暖白
const CORAL = 0xeb5147; // 角落紅點
const PRESS_TINT = 0xcdd8ca; // 按下時霧面暗化
const PRESS_OFFSET = 1; // 按下下移
const FOCUS_WIDTH = 2; // 焦點輪廓粗細
const FOCUS_GAP = 3; // 輪廓距按鈕
const HOVER_ALPHA = 0.45; // 滑鼠焦點淡邊
const FOCUS_ALPHA = 0.95; // 鍵盤焦點明邊
const BUTTON_CORNER_RATIO = 0.45; // 焦點圓角比例
const SELECTED_MARK_WIDTH = 2; // 選中底線
const SELECTED_MARK_BOTTOM = 6; // 選中底線距底
const SELECTED_MARK_RATIO = 0.25; // 底線半寬比例
const BUTTON_SOURCE_CAP_RATIO = 0.5; // 三宮格固定左右半圓
const PANEL_SOURCE_CAP_RATIO = 0.16; // 九宮格的來源角落比例
const PANEL_CORNER_SIZE = 76; // C 粗框的九宮格角落尺寸
const PANEL_OUTSET = 16; // 粗框向外擴展，保留原內容留白
const PANEL_OUTSET_HEIGHT_RATIO = 0.04; // 矮面板限制外擴，避免列與列重疊
const PANEL_MAX_CORNER_RATIO = 0.45; // 矮面板保留中央伸展區
const PANEL_PIP_INSET = 34; // 紅點距框邊
const PANEL_PIP_RADIUS = 7; // 框角紅點半徑
const MASCOT_WIDTH = 104; // 彈窗探頭寬度
const MASCOT_LEFT_INSET = 32; // 探頭距左邊
const MASCOT_EDGE_OVERLAP = 9; // 小手搭在上框

function createButtonTexture(
  scene: Phaser.Scene,
  key: string,
  width: number,
  height: number
): Phaser.GameObjects.NineSlice {
  const frame = scene.textures.getFrame(key, CREAM_ASSET_FRAME);
  const scale = height / frame.height;
  const cap = Math.ceil(frame.height * BUTTON_SOURCE_CAP_RATIO);
  return scene.add
    .nineslice(0, 0, key, CREAM_ASSET_FRAME, width / scale, frame.height, cap, cap)
    .setScale(scale);
}

export class CDialogButtonSurface extends Phaser.GameObjects.Container {
  private readonly red: Phaser.GameObjects.NineSlice;
  private readonly green: Phaser.GameObjects.NineSlice;
  private readonly focus: Phaser.GameObjects.Graphics;
  private selected = false;
  private primary = false;
  private hovered = false;
  private focused = false;
  private pressed = false;
  private motionPressed = false;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    height: number,
    selected: boolean
  ) {
    super(scene, x, y);
    this.red = createButtonTexture(scene, CREAM_BUTTON_RED_KEY, width, height);
    this.green = createButtonTexture(scene, CREAM_BUTTON_GREEN_KEY, width, height);
    this.focus = scene.add.graphics();
    this.add([this.green, this.red, this.focus]);
    this.setSize(width, height);
    this.selected = selected;
    scene.add.existing(this);
    this.refreshAppearance();
  }

  setPrimary(primary = true): this {
    this.primary = primary;
    this.refreshAppearance();
    return this;
  }

  setSelected(selected: boolean): this {
    this.selected = selected;
    this.refreshAppearance();
    return this;
  }

  setFocused(focused: boolean): this {
    this.focused = focused;
    this.refreshAppearance();
    return this;
  }

  setAppearance(selected: boolean, hovered: boolean, pressed: boolean): void {
    this.selected = selected;
    this.hovered = hovered;
    this.pressed = pressed;
    this.refreshAppearance();
  }

  bindInteraction(target: Phaser.GameObjects.GameObject): this {
    target.on('pointerover', () => {
      this.hovered = true;
      this.refreshAppearance();
    });
    target.on('pointerout', () => {
      this.hovered = false;
      this.pressed = false;
      this.refreshAppearance();
    });
    target.on('pointerdown', () => {
      this.pressed = true;
      this.refreshAppearance();
    });
    target.on('pointerup', () => {
      this.pressed = false;
      this.refreshAppearance();
    });
    target.on('pointerupoutside', () => {
      this.pressed = false;
      this.refreshAppearance();
    });
    return this;
  }

  private refreshAppearance(): void {
    if (!this.scene) return;
    if (this.motionPressed !== this.pressed) {
      this.motionPressed = this.pressed;
      const scale = this.pressed && !reducedMotion() ? Motion.pressScale : 1;
      animateTo(
        this.scene,
        this,
        { scaleX: scale, scaleY: scale },
        this.pressed ? 0 : Motion.pressMs
      );
    }
    const isRed = this.primary || this.selected;
    this.red.setVisible(isRed);
    this.green.setVisible(!isRed);
    for (const surface of [this.red, this.green]) {
      surface.setY(this.pressed ? PRESS_OFFSET : 0);
      if (this.pressed) surface.setTint(PRESS_TINT);
      else surface.clearTint();
    }
    const g = this.focus.clear();
    if (this.focused || this.hovered) {
      g.lineStyle(FOCUS_WIDTH, CREAM, this.focused ? FOCUS_ALPHA : HOVER_ALPHA);
      g.strokeRoundedRect(
        -this.width / 2 - FOCUS_GAP,
        -this.height / 2 - FOCUS_GAP,
        this.width + FOCUS_GAP * 2,
        this.height + FOCUS_GAP * 2,
        this.height * BUTTON_CORNER_RATIO
      );
    }
    if (this.selected) {
      g.lineStyle(SELECTED_MARK_WIDTH, CREAM);
      g.lineBetween(
        -this.width * SELECTED_MARK_RATIO,
        this.height / 2 - SELECTED_MARK_BOTTOM,
        this.width * SELECTED_MARK_RATIO,
        this.height / 2 - SELECTED_MARK_BOTTOM
      );
    }
  }
}

export function createDialogPanel(
  scene: Phaser.Scene,
  x: number,
  y: number,
  width: number,
  height: number,
  withMascot = true
): Phaser.GameObjects.Container {
  const frame = scene.textures.getFrame(CREAM_PANEL_KEY, CREAM_ASSET_FRAME);
  const cap = Math.ceil(Math.min(frame.width, frame.height) * PANEL_SOURCE_CAP_RATIO);
  const cornerSize = Math.min(PANEL_CORNER_SIZE, Math.min(width, height) * PANEL_MAX_CORNER_RATIO);
  const scale = cornerSize / cap;
  const outset = Math.min(PANEL_OUTSET, height * PANEL_OUTSET_HEIGHT_RATIO);
  const panelWidth = width + outset * 2;
  const panelHeight = height + outset * 2;
  const panel = scene.add
    .nineslice(
      0,
      0,
      CREAM_PANEL_KEY,
      CREAM_ASSET_FRAME,
      panelWidth / scale,
      panelHeight / scale,
      cap,
      cap,
      cap,
      cap
    )
    .setScale(scale);
  const root = scene.add.container(x, y, [panel]).setSize(width, height);
  if (withMascot) {
    const peekFrame = scene.textures.getFrame(CREAM_MASCOT_PEEK_KEY, CREAM_ASSET_FRAME);
    const mascot = scene.add
      .image(
        -width / 2 + MASCOT_LEFT_INSET,
        -panelHeight / 2 + MASCOT_EDGE_OVERLAP,
        CREAM_MASCOT_PEEK_KEY,
        CREAM_ASSET_FRAME
      )
      .setOrigin(0, 1)
      .setScale(MASCOT_WIDTH / peekFrame.width);
    root.add(mascot);
  }
  const pipScale = cornerSize / PANEL_CORNER_SIZE;
  const pipInset = PANEL_PIP_INSET * pipScale;
  const pips = scene.add.graphics().fillStyle(CORAL);
  for (const side of [-1, 1]) {
    pips.fillCircle(
      side * (panelWidth / 2 - pipInset),
      panelHeight / 2 - pipInset,
      PANEL_PIP_RADIUS * pipScale
    );
  }
  root.add(pips);
  return root;
}

export function createDialogButtonSurface(
  scene: Phaser.Scene,
  x: number,
  y: number,
  width: number,
  height: number,
  active = false
): CDialogButtonSurface {
  return new CDialogButtonSurface(scene, x, y, width, height, active);
}
